import { CreditCardFilled, FileExcelOutlined, UndoOutlined } from '@ant-design/icons';
import type { ColDef } from 'ag-grid-community';
import {
    AllCommunityModule,
    CheckboxEditorModule,
    ClientSideRowModelModule,
    ModuleRegistry,
    themeQuartz,
    ValidationModule,
    ValueFormatterParams
} from "ag-grid-community";
// import 'ag-grid-community/styles/ag-grid.css';
// import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { Button, message } from 'antd';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { DownloadExcelAccount } from '../api/excelAccount';
import { FectchAccounting } from "../api/getAccount";
import { getPaymentUrl } from "../api/getPaymentUrl";
import CustomLoadingOverlay from '../components/CustomLoadingOverlay';
import { AccountData, FecthAccountResponse, FecthAccountResult, FilingProps } from '../constant';

ModuleRegistry.registerModules([AllCommunityModule, CheckboxEditorModule, ClientSideRowModelModule, ValidationModule]);

const HeaderStyle: React.CSSProperties = {
    height: '50px',
};

const isAccountingResponse = (result: FecthAccountResult): result is FecthAccountResponse => {
    return result.success === true;
}

const Accounting: React.FC<FilingProps> = ({ user, isSelected }) => {

    const [loading, setLoading] = useState(false)
    const [rowData, setRowData] = useState<AccountData[]>([]);
    const [colDefs, setColDefs] = useState<ColDef[]>([
        {
            headerName: 'Type', field: "transID", width: 100, resizable: true,
            valueFormatter: (params: any) => {
                if (params.data?.postSequenceID === 0)
                    return "Payment"
                else
                    return "Invoice"
            },
        },
        {
            headerName: 'Date', field: "transDate", width: 100, resizable: true,
            valueFormatter: (params) => {
                const value = params.value;
                return value ? moment(value).format('MM/DD/YYYY') : '';
            },
            cellEditor: "agDateCellEditor"
        },
        { headerName: 'Invoice #', field: "postSequenceID", width: 100, resizable: true },
        {
            headerName: 'Check #', field: "checkNumber", width: 100, resizable: true,
            valueFormatter: (params: ValueFormatterParams<string>) => {
                if (params.value === "0")
                    return ""
                else
                    return params.value
            }
        },
        {
            headerName: 'Amount', field: "amount", width: 100, resizable: true, cellStyle: { textAlign: "right" },
            valueFormatter: (params: any) => {
                return parseFloat(params.value).toFixed(2);
            },
        },
        {
            headerName: 'Balance', field: "balance", width: 100, resizable: true, cellStyle: { textAlign: "right" },
            valueFormatter: (params: any) => {
                return parseFloat(params.value).toFixed(2);
            },
        },
    ]);

    const FetchAccount = () => {

        setLoading(true)

        FectchAccounting(user.licenseNumber)
            .then((fetchAccountingResult) => {
                if (fetchAccountingResult.success) {
                    //message.success({ content: 'Accounting Successfully', key: 'AccountStatus', duration: 2  });
                    message.destroy('AccountStatus');
                    if (isAccountingResponse(fetchAccountingResult)) {
                        setRowData(fetchAccountingResult.record)
                    }
                }
                else {
                    if ("errorMsg" in fetchAccountingResult) {
                        message.error({ content: 'Failed to Accounting.', key: 'AccountStatus', duration: 2 });
                        //message.error(fetchAccountingResult.errorMsg)
                    }
                }
            })
            .catch((error) => {
                console.error("An error occurred:", error.message);
            })
            .finally(() => {
                setLoading(false)
            });
    }

    useEffect(() => {
        FetchAccount();
    }, []);

    useEffect(() => {
        if (!isSelected) {
            return;
        }
        FetchAccount();
    }, [user.licenseNumber]);

    const handlePayUrlBtn = () => {
        getPaymentUrl()
            .then((response) => {
                if (response.success) {
                    console.log(`get url = ${response.msg}`)
                    window.open(response.msg, '_blank');
                }
                else {
                    message.error(response.msg)
                }
            })
            .catch((error) => {
                console.error("An error occurred:", error.message);
                return
            });
    }

    const handleRefreshBtn = () => {
        FetchAccount();
    }

    const handleExcelBtn = () => {
        DownloadExcelAccount(user.licenseNumber)
            .then((response) => {
                if (response.success) {
                    message.success(response.errorMsg)
                }
                else {
                    message.error(response.errorMsg)
                }
            })
            .catch((error) => {
                console.error("An error occurred:", error.message);
                return
            });
    }

    const defaultColDef = useMemo<ColDef>(() => {
        return {
            headerClass: 'custom-header',
        };
    }, []);

    const myTheme = themeQuartz
        .withParams({
            headerColumnBorder: { color: 'gray' },
            headerColumnBorderHeight: '90%',
        });

    return (
        <div style={{ height: '100%' }} className='text-style'>
            <div className="flex justify-between items-center bg-gray-100" style={HeaderStyle}>
                <div className='flex flex-row ml-10 text-xs'>
                    <div><span>This is a listing of all invoices (filings) and payments made on or after 11/1/2010. A running balance is also shown in the far right column.  </span></div>
                </div>

                <div className="flex flex-row-reverse bg-gray-100 items-center" style={HeaderStyle}>
                    <Button
                        variant="outlined"
                        type='primary'
                        onClick={handleExcelBtn}
                        icon={<FileExcelOutlined className='icon-style' />}
                        className='ml-0.5, mr-1.5 button-style'>
                        Save to Excel
                    </Button>
                    <Button
                        variant="outlined"
                        type='primary'
                        onClick={handleRefreshBtn}
                        icon={<UndoOutlined className='icon-style' />}
                        className='ml-0.5, mr-1.5 button-style'>
                        Refresh
                    </Button>
                    <Button
                        variant="outlined"
                        type='primary'
                        onClick={handlePayUrlBtn}
                        icon={<CreditCardFilled className='icon-style' />}
                        className='ml-0.5, mr-1.5 button-style'>
                        Pay Online
                    </Button>
                </div>
            </div>
            <div className="ag-theme-alpine tab-container">
                <AgGridReact
                    rowData={rowData}
                    columnDefs={colDefs}
                    theme={myTheme}
                    defaultColDef={defaultColDef}
                    headerHeight={35}
                    rowHeight={25}
                    loading={loading}
                    loadingOverlayComponent={CustomLoadingOverlay}
                    loadingOverlayComponentParams={{ message: 'Loading...' }}
                />
            </div>
        </div>
    )
}

export default Accounting;